import { IOrganization } from '@/pages/Organizations/organizations.types.ts'
import { useMemo, useState } from 'react'
import useOrganizationUsers from '@/pages/Users/hooks/useOrganizationUsers.ts'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import organizationsApi from '@/pages/Organizations/organizations.api.ts'
import organizationsEvents from '@/pages/Organizations/events/organizations.events.ts'
import KeyValueTable from '@/shared/Tables/KeyValueTable'
import Tag from '@/shared/Tags/Tag'
import SeparateLine from '@/shared/Forms/SeparateLine'
import css from '@/pages/Organizations/components/OrganizationSidePage/organizationSidePage.module.css'
import timeUtils from '@/utils/time.utils.ts'
import Tabs from '@/shared/Tabs'
import FormEditButton from '@/shared/Buttons/FormEditButton'
import FormDeleteButton from '@/shared/Buttons/FormDeleteButton'
import { ITab } from '@/shared/Tabs/tabs.types.ts'
import GoToLinkButton from '@/shared/Buttons/GoToLinkButton'
import urls from '@/navigation/urls.ts'
import { useRecoilValue } from 'recoil'
import equipmentAtom from '@/pages/Equipment/atoms/equipment.atom.ts'
import equipmentUtils from '@/pages/Equipment/equipment.utils.ts'
import EquipmentStatus from '@/pages/Equipment/components/Common/EquipmentStatus'
import { NavLink } from 'react-router-dom'
import ProtectedContent from '@/services/rights/ProtectedContent'
import userUtils from '@/pages/User/user.utils'
import Image from '@/shared/Common/Image'
import useAccessToken from '@/pages/Auth/hooks/useAccessToken.ts'
import config from '@/config.ts'

interface IProps {
  organization: IOrganization
  onEdit: () => void
  onClose: () => void
}

type TTab = 'users' | 'equipment'
const tabs: ITab<TTab>[] = [
  { title: 'Сотрудники', id: 'users' },
  { title: 'Устройства', id: 'equipment' },
]

const ViewOrganizationSidePageContent = (props: IProps) => {
  const [selectedTab, setSelectedTab] = useState<TTab>('users')
  const { loading: usersLoading, users: organizationUsers } = useOrganizationUsers(props.organization.id)
  const accessToken = useAccessToken()

  const equipmentList = useRecoilValue(equipmentAtom).items
  const organizationEquipment = useMemo(() => {
    return equipmentList.filter((e) => e.organizations.some((e) => e.organization.id === props.organization.id))
  }, [equipmentList, props.organization.id])

  const handleRemove = () => {
    confirmService.requestConfirm({
      description: `Подтвердите удаление организации ${props.organization.name}`,
      buttonType: 'primary',
      onConfirm: () =>
        organizationsApi
          .remove(props.organization.id)
          .then((r) => r.status === 'success' && organizationsEvents.deleted(r.body.id))
          .then(() => props.onClose()),
    })
  }

  const organizationImageUrl = useMemo(() => {
    if (!props.organization.image) return null
    return `${config.API_URL}/nsi/v1/organizations/${props.organization.id}/image?accessToken=${accessToken}`
  }, [props.organization.image, props.organization.id, accessToken])

  const renderTabContent = () => {
    if (selectedTab === 'users') {
      return (
        <div className="flex-lines gap16">
          <div className="inline-flex-gap width100 flex-space-between">
            <h5 className="text-heading-4">Сотрудники</h5>
            <GoToLinkButton to={urls.organizationUsers(props.organization.id)} />
          </div>
          <KeyValueTable
            loading={usersLoading}
            values={organizationUsers.map((user) => ({
              key: user.id,
              title: userUtils.getFullName(user.userInfo),
              value: user.active ? <Tag color="green">Активен</Tag> : <Tag color="red">Деактивирован</Tag>,
            }))}
          />
        </div>
      )
    }

    const equipmentValues = organizationEquipment.map((e) => ({
      key: e.id,
      title: (
        <NavLink to={'/equipment/lifts/' + e.id} style={{ textDecoration: 'none' }} className="text-link text-nm">
          {equipmentUtils.getEquipmentAddress(e.location)}
        </NavLink>
      ),
      value: <EquipmentStatus equipment={e} />,
    }))

    return (
      <div className="flex-lines" style={{ minHeight: 0, overflow: 'auto' }}>
        <KeyValueTable values={equipmentValues} itemStyle={{ gridTemplateColumns: '2fr 1fr' }} />
      </div>
    )
  }

  return (
    <>
      <div className="flex-lines gap16" style={{ height: '100%' }}>
        <div className="inline-flex-gap gap16 center">
          {organizationImageUrl && (
            <div className={css.imageWrapper}>
              <Image
                src={organizationImageUrl}
                alt={props.organization.name}
                wrapperClassname={css.imageFrame}
                className={css.image}
              />
            </div>
          )}
          <h4 className="text-heading-4">{props.organization.name}</h4>
        </div>
        <SeparateLine />
        <div className={css.sidePageGrid}>
          <p className="text-nm text-secondary">ИНН:</p>
          <p className="text-nm">{props.organization.tin || 'Не указан'}</p>
          <p className="text-nm text-secondary">Контактное лицо:</p>
          <p className="text-nm">{props.organization.contact}</p>
          <p className="text-nm text-secondary">Дата создания:</p>
          <p className="text-nm">
            {timeUtils.formatDate(new Date(props.organization.createdAt), 'dd MMM yyyy, HH:mm')}
          </p>
          <p className="text-nm text-secondary">Дата обновления:</p>
          <p className="text-nm">
            {timeUtils.formatDate(new Date(props.organization.updatedAt), 'dd MMM yyyy, HH:mm')}
          </p>
        </div>
        <SeparateLine />
        <Tabs tabs={tabs} onSelect={setSelectedTab as any} selectedTab={selectedTab} />
        {renderTabContent()}
        <ProtectedContent requiredOneOf={['organizations:delete', 'parameters:update']}>
          <div style={{ marginTop: 'auto' }}>
            <SeparateLine />
          </div>
          <div className="inline-flex-gap gap8">
            <ProtectedContent requiredRight="organizations:update">
              <FormEditButton onClick={props.onEdit} />
            </ProtectedContent>
            <ProtectedContent requiredRight="organizations:delete">
              <FormDeleteButton onClick={handleRemove} />
            </ProtectedContent>
          </div>
        </ProtectedContent>
      </div>
    </>
  )
}

export default ViewOrganizationSidePageContent
